import { Component } from "react"
import { Modal, Button, Form, Table, Spinner } from "react-bootstrap"
import { Subscription } from "rxjs"
import RoleData from "../../api/Administration/Model/RoleData"
import { toast } from "react-toastify"
import GetAdditionalRoles from "../../api/Roles/GetAdditionalRole"
import Role from "../../api/Roles/Model/Role"
import Checkbox from "../Checkbox"
import TimeZoneData from "../../api/Administration/Model/TimeZoneData"
import AutoFilterLocationsGet, { GetUserLocationsAdministrators } from "../../api/Location/AutoFilterLocationsGet";
import AutoFilterLocation from "../../api/Location/LocationModel/AutoFilterLocation";
import {AppState} from "../../AppState";
import {GetSelectedAssignedClinic} from "../../authentication/SelectedCompany/CompanySelectionDrivenSelectors";
import {connect} from "react-redux";
import {isLocationAdmin, isPortalAdmin} from "../../authentication/isAdminRole";
import UserInformation from "../../api/Administration/Model/UserInformation";
import CheckboxList from "../CheckboxList"

interface AdditionalRoleModalProps {
  show: boolean
  data: RoleData[]
  onClose: () => void
  onSave: (data: RoleData[]) => void
  onSaveTimeZoneId: (timeZoneId?: number) => void
  timeZoneId?: number
  timeZones: TimeZoneData[]
  userInfo?: UserInformation
}

interface AdditionalRoleModalAuthProps {
  companyID: number
  isPortalAdmin: boolean
  isLocationAdmin: boolean
}

interface AdditionalRoleModalState {
  open: boolean
  loading: boolean
  roles: Role[]
  data: RoleData[]
  timeZoneId?: number
  locations: AutoFilterLocation[]
  selectedLocations: string[]
}

const LocationAdministrator = "Location Administrator"

export class AdditionalRoleModal extends Component<AdditionalRoleModalProps & AdditionalRoleModalAuthProps, AdditionalRoleModalState> {
  state: AdditionalRoleModalState = { open: this.props.show, loading: false, roles: [], data: [], timeZoneId: this.props.timeZoneId, locations: [], selectedLocations: [] }
  subscription = new Subscription()

  mapRolesToRoleData = (roles: Role[], data: RoleData[] = this.state.data): RoleData[] => roles.map(role => {
    const existData = data.find((data: RoleData) => data.RoleID === role.RoleID)
    if (existData) return { ...existData }
    else return { RoleID: role.RoleID, Description: role.Description, Level: role.RoleID, Title: role.Title, Active: false }
  })

  componentDidMount() {
    this.setState({ loading: true })
    this.subscription.add(
      GetAdditionalRoles().subscribe(
      roles => this.setState({ roles: roles, data: this.mapRolesToRoleData(roles) }),
      (error: string) => toast.error(error),
      () => this.setState({ loading: false })
    ))
  }

  loadLocations = () => {
    if (this.props.userInfo) {
      this.subscription.add(
          AutoFilterLocationsGet(this.props.userInfo.CompanyID, "", "", this.props.userInfo.ImplementationID).subscribe(
              data => this.setState({locations: data})
          )
      )

      this.subscription.add(
        GetUserLocationsAdministrators(this.props.userInfo.CompanyID, this.props.userInfo.ApplicationUserID).subscribe(
          (data: string[]) => {
            this.setState({selectedLocations: data})

            const newData: RoleData[] = [...this.state.data]
            const locationRole = newData.find(role => role.Description == LocationAdministrator)
            if (locationRole) {
              locationRole.Active = data.length > 0
              locationRole.Locations = data
              this.setState({ data: newData })
            }
          }
        )
      )
    }
  }

  componentWillUnmount() {
    this.subscription && this.subscription.unsubscribe()
  }

  componentDidUpdate(prevProps: AdditionalRoleModalProps) {
    if (this.props.show !== prevProps.show) {
      this.setState({ open: this.props.show })
    }
    if (this.props.timeZoneId !== prevProps.timeZoneId) {
      this.setState({ timeZoneId: this.props.timeZoneId })
    }

    if (this.props.data !== prevProps.data) {
      this.setState({ data: this.mapRolesToRoleData(this.state.roles, this.props.data) })
    }

    if (this.props.userInfo?.ApplicationUserID !== prevProps.userInfo?.ApplicationUserID) {
      this.loadLocations()
    }
  }

  handleClose = () => {
    this.setState({ open: false, data: this.mapRolesToRoleData(this.state.roles, this.props.data) })
    this.props.onClose()
  }

  handleCloseLocations = (items: string[]) => {
    const newData: RoleData[] = [...this.state.data]
    const locationRole = newData.find(role => role.Description == LocationAdministrator)
    if (locationRole) {
      locationRole.Active = items.length > 0
      locationRole.Locations = items
    }
    this.setState({data: newData, selectedLocations: items})
  }

  handleSave = () => {
    const data: RoleData[] = this.state.data
    if (data.every(item => !item.Active)) {
      toast.error("Please select at least one active role")
      return
    }
    data.forEach(i => { if (i.Title == LocationAdministrator && i.Active) { i.Locations = this.state.selectedLocations } })

    this.props.onSaveTimeZoneId(this.state.timeZoneId == 0 ? this.props.timeZones[0].TimeZoneID : this.state.timeZoneId)
    this.props.onSave(data)
  }

  handleTimeZoneChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    this.setState({ timeZoneId: parseInt(event.target.value) })
  }

  toggleActive = (roleID: number) => {
    const newData: RoleData[] = [...this.state.data]
    const updatedRole = newData.find(role => role.RoleID === roleID)
    if (updatedRole) {
      updatedRole.Active = !updatedRole.Active
      this.setState({ data: newData })
    }
  }

  DataRow = (role: Role, index: number) => {
    const data: RoleData[] = this.state.data
    const item = data.find((data: RoleData) => data.RoleID === role.RoleID)
    const checked = item?.Active
    return <tr key={role.RoleID}>
      <td className="align-middle">{role.Title}</td>
      <td className="align-middle" ><Checkbox id={`toggleActive${role.RoleID}`} label="Active" checked={checked} onChange={() => this.toggleActive(role.RoleID)} className="mr-auto" /></td>
      <td className="text-left align-middle">
        {role.Description === 'Collector' && <>
          <Form.Label className="col-form-label">Timezone</Form.Label>&nbsp;
          <Form.Control style={{ width: "40%", display: "inline" }} onChange={this.handleTimeZoneChange} value={this.state.timeZoneId?.toString()} className="col-form-label" id="timeZones" as="select" disabled={!item?.Active}>
            {this.props.timeZones.map(tz => <option value={tz.TimeZoneID} key={tz.TimeZoneID}>{tz.Abbreviation}</option>)}
          </Form.Control>
        </>
        }
        {!this.props.isLocationAdmin && role.Description === LocationAdministrator &&
          <CheckboxList
            title='Locations'
            items={this.state.locations.map(i => { return { label: i.Name, value: i.AccountID?.toString() }})}
            selectedValues={this.state.selectedLocations.map(i => i.toString())}
            onClose={items => this.handleCloseLocations(items)}
            disabled={!checked}
          />
        }
      </td>
    </tr>
  }

  render = () => {
    if (!this.state.open) {
      return <></>
    }
    const roles: Role[] = this.state.roles

    return (
      <Modal show={this.state.open} onHide={this.handleClose} size="xl" backdrop="static" dialogClassName="dark">
        <Modal.Header closeButton>
          <Modal.Title>Enter Additional Roles</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Table bordered responsive className="dark text-center">
            <thead><tr><th className="w-50">Role</th><th className="w-40">Status</th><th className="w-20">&nbsp;</th></tr></thead>
            <tbody>{this.state.loading ? <tr><td colSpan={2}><Spinner animation="border" size="sm" /></td></tr> :
              roles.map((role, index) => this.DataRow(role, index))}</tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={this.handleClose}>Cancel</Button>
          <Button onClick={this.handleSave}>Confirm</Button>
        </Modal.Footer>
      </Modal>
    )
  }
}

function mapStateToProps(state: AppState): AdditionalRoleModalAuthProps {
  const selectedCompany =  GetSelectedAssignedClinic(state)
  return {
    companyID: selectedCompany?.AccountID || 0,
    isPortalAdmin: isPortalAdmin(state),
    isLocationAdmin: isLocationAdmin(state)
  }
}
export default connect(mapStateToProps)(AdditionalRoleModal)